/* eslint-disable no-unused-expressions */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnamesBind from 'classnames/bind';
import chain from 'chain-function';
import useUniqueId from '@concur/nui-widgets/lib/utils/useUniqueId';
import Button from '@concur/nui-widgets/Button';
import Icon from '@concur/nui-widgets/Icon';
import Overlay from '@concur/nui-widgets/Overlay';
import Tooltip from '@concur/nui-widgets/Tooltip';

const MenuItemToggle = React.forwardRef((props, ref) => {
    const {
        buttonAriaLabel,
        buttonClassName,
        buttonProps,
        children,
        className,
        styles,
        content,
        cssBlock,
        hideCaret,
        onClick = () => {},
        onClose = () => {},
        onShow = () => {},
        overlayProps,
        popperPlacement = 'bottom-start',
        tooltipMessage,
        doNotDescribe,
        open = false,
        iconOnly,
    } = props;

    const classnames = classnamesBind.bind(styles);
    const [expanded, setExpanded] = useState(open);

    useEffect(() => {
        setExpanded(open);
    }, [open]);

    let buttonId = useUniqueId();
    if (buttonProps?.id) {
        buttonId = buttonProps.id;
    }

    const handleToggleClick = (e) => {
        setExpanded(!expanded);
        onClick(e, !expanded);
    };

    const handleClose = () => {
        setExpanded(false);
        onClose();

        requestAnimationFrame(() => {
            document.getElementById(buttonId)?.focus();
        });
    };

    const classes = classnames(`${cssBlock}__listitem`, className);

    const getButtonContent = (additionalProps) => (
        <Button
            {...additionalProps}
            {...buttonProps}
            aria-label={buttonAriaLabel}
            className={classnames(`${cssBlock}__button-outer`)}
            innerProps={{
                ...buttonProps?.innerProps,
                className: classnames(`${cssBlock}__button`, buttonClassName, {
                    [`${cssBlock}__button--icon`]: iconOnly,
                    [`${cssBlock}__button--active`]: expanded,
                }),
            }}
            id={buttonId}
            innerTextClasses={iconOnly ? classnames(`${cssBlock}__button-inner-text`, buttonProps?.innerTextClasses) : buttonProps?.innerTextClasses}
            onClick={handleToggleClick}
            role="menuitem"
        >
            {content}
            {!hideCaret && <Icon ariaHidden iconName="arrow-2-s" forceSize="xs-plus" />}
        </Button>
    );

    return (
        <li className={classes} ref={ref} role="none">
            <Overlay
                {...overlayProps}
                disableEdgeDetection
                onHide={handleClose}
                onShow={chain(() => setExpanded(true), () => onShow(true))}
                popperPlacement={popperPlacement}
                show={expanded}
                toggleOverlay
                renderTargetComponent={(
                    popperTargetRef,
                    referenceProps,
                    targetClasses,
                    targetProps,
                ) => {
                    const buttonContent = getButtonContent({
                        ...referenceProps,
                        ...targetProps,
                        ref: popperTargetRef,
                    });
                    return tooltipMessage && !expanded ? (
                        <Tooltip
                            delay={1000}
                            ref={popperTargetRef}
                            doNotDescribe={doNotDescribe}
                            placement={['bottom', 'top', 'right', 'left']}
                            message={tooltipMessage}
                            outerPopperProps={{
                                showArrow: false,
                                offset: 3,
                            }}
                            innerContainerProps={{
                                className: classnames(`${cssBlock}__tooltip-inner`, className),
                            }}
                        >
                            {buttonContent}
                        </Tooltip>
                    ) : (
                        buttonContent
                    );
                }}
                targetClassName={classnames(`${cssBlock}__target`)}
            >
                {children}
            </Overlay>
        </li>
    );
});

MenuItemToggle.displayName = 'MenuItemToggle';

MenuItemToggle.propTypes = {
    buttonAriaLabel: PropTypes.string,
    buttonClassName: PropTypes.string,
    buttonProps: PropTypes.object,
    children: PropTypes.node,
    className: PropTypes.string,
    content: PropTypes.node,
    onClick: PropTypes.func,
    onClose: PropTypes.func,
    onShow: PropTypes.func,
    open: PropTypes.bool,
    overlayProps: PropTypes.object,
    cssBlock: PropTypes.string,
    hideCaret: PropTypes.bool,
    popperPlacement: PropTypes.string,
    iconOnly: PropTypes.bool,
    styles: PropTypes.object,
    tooltipMessage: PropTypes.string,
    doNotDescribe: PropTypes.bool,
};

export default MenuItemToggle;
